/**
 * AdminTopSeriesTab.jsx — admin/co-admin tile for the Library Features
 * Rail "Top Series" pin (Jun 3 2026). Lets the admin search every
 * non-vault series, pin / unpin, and reorder the pinned list. Cap = 12.
 *
 * Same backend the per-row pin button uses: POST/DELETE
 *   /api/admin/library/featured/pin/{series_id}
 * Plus a bulk POST /api/admin/library/featured for reorder.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Pin, PinOff, Search, ChevronUp, ChevronDown, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PIN_CAP = 12;

const AdminTopSeriesTab = () => {
  const [allSeries, setAllSeries] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [query, setQuery] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const [libRes, featRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/library`),
        authFetch(`${BACKEND_URL}/api/library/featured`),
      ]);
      if (libRes.ok) {
        const d = await libRes.json();
        const list = (d.series || []).filter((s) => !s.is_vault && s.series_id);
        setAllSeries(list);
      }
      if (featRes.ok) {
        const d = await featRes.json();
        setFeatured(d.top_series || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const pinnedIds = useMemo(
    () => new Set((featured || []).map((s) => s.series_id || s.book_id)),
    [featured],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSeries;
    return allSeries.filter((s) =>
      (s.title || '').toLowerCase().includes(q)
      || (s.author || '').toLowerCase().includes(q),
    );
  }, [allSeries, query]);

  const toggle = async (item) => {
    const sid = item.series_id || item.book_id;
    if (!sid) return;
    const isPinned = pinnedIds.has(sid);
    if (!isPinned && featured.length >= PIN_CAP) {
      toast.error(`Top Series is capped at ${PIN_CAP}. Unpin one first.`);
      return;
    }
    setBusyId(sid);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/library/featured/pin/${encodeURIComponent(sid)}`,
        { method: isPinned ? 'DELETE' : 'POST' },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Pin action failed.');
        return;
      }
      toast.success(isPinned ? `Unpinned "${item.title || sid}"` : `Pinned "${item.title || sid}"`);
      await refresh();
    } catch {
      toast.error('Network error.');
    } finally { setBusyId(''); }
  };

  const reorder = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= featured.length) return;
    const next = [...featured];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setFeatured(next);  // optimistic
    setBusyId('reorder');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/library/featured`, {
        method: 'POST',
        body: JSON.stringify({
          series_ids: next.map((s) => s.series_id || s.book_id),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Reorder failed.');
        await refresh();  // revert by re-pulling truth
        return;
      }
      toast.success('Order updated.');
    } finally { setBusyId(''); }
  };

  return (
    <div className="space-y-4" data-testid="admin-top-series-tab">
      {/* Pinned list */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Pin className="w-4 h-4 text-amber-300" />
            Top Series — pinned ({featured.length}/{PIN_CAP})
          </h3>
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        </div>
        {featured.length === 0 ? (
          <p className="text-sm text-slate-400">
            No series pinned yet. Pin up to {PIN_CAP} from the list below to populate the Library "Top Series" rail.
          </p>
        ) : (
          <ol className="space-y-2" data-testid="admin-top-series-pinned-list">
            {featured.map((s, idx) => {
              const sid = s.series_id || s.book_id;
              const cover = resolveCoverUrl(s);
              return (
                <li
                  key={sid}
                  className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/50 border border-slate-700"
                  data-testid={`admin-top-series-pinned-${sid}`}
                >
                  <span className="text-[10px] uppercase tracking-widest font-black text-amber-300 w-6 text-center">
                    #{idx + 1}
                  </span>
                  <div className="w-9 h-12 rounded bg-slate-800 border border-slate-700 overflow-hidden flex-shrink-0">
                    {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <BookOpen className="w-4 h-4 text-slate-500 m-auto mt-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{s.title || 'Untitled'}</p>
                    {s.author && <p className="text-[11px] text-slate-400 truncate">{s.author}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => reorder(idx, -1)}
                    disabled={idx === 0 || busyId === 'reorder'}
                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-30"
                    title="Move up"
                    data-testid={`admin-top-series-up-${sid}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => reorder(idx, 1)}
                    disabled={idx === featured.length - 1 || busyId === 'reorder'}
                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-30"
                    title="Move down"
                    data-testid={`admin-top-series-down-${sid}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(s)}
                    disabled={busyId === sid}
                    className="p-1.5 rounded text-amber-300 hover:bg-amber-500/20 disabled:opacity-40"
                    title="Unpin"
                    data-testid={`admin-top-series-unpin-${sid}`}
                  >
                    <PinOff className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Search + library list */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
        <h3 className="text-white font-bold mb-3">Pin from library</h3>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title or author…"
            className="w-full bg-slate-900/60 border border-slate-700 rounded-lg text-white text-sm pl-9 pr-3 py-2 placeholder-slate-500 focus:outline-none focus:border-amber-400/50"
            data-testid="admin-top-series-search"
          />
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No matches.</p>
        ) : (
          <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-top-series-pool">
            {filtered.map((s) => {
              const sid = s.series_id || s.book_id;
              const isPinned = pinnedIds.has(sid);
              const cover = resolveCoverUrl(s);
              return (
                <li
                  key={sid}
                  className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/40 border border-slate-700/50 hover:border-slate-600"
                  data-testid={`admin-top-series-row-${sid}`}
                >
                  <div className="w-9 h-12 rounded bg-slate-800 border border-slate-700 overflow-hidden flex-shrink-0">
                    {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <BookOpen className="w-4 h-4 text-slate-500 m-auto mt-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{s.title || 'Untitled'}</p>
                    {s.author && <p className="text-[11px] text-slate-400 truncate">{s.author}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(s)}
                    disabled={busyId === sid}
                    className={`px-3 py-1.5 rounded text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50 ${
                      isPinned
                        ? 'bg-amber-500/15 text-amber-200 border border-amber-300/40 hover:bg-amber-500/25'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                    data-testid={`admin-top-series-toggle-${sid}`}
                  >
                    {isPinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                    {isPinned ? 'Unpin' : 'Pin'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminTopSeriesTab;
